import Accordion from "react-bootstrap/Accordion";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { request } from "../../../../../hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { SessionSummary } from "./SessionSummary";
import styled from "styled-components";
import { logSessionHistory, LogLevel } from "../../../../../utils/sentryLogger";
import { colors } from "utils";
import { trackViewedPage } from "utils/analytics";
import { sessionDurationMs } from "utils/sessionDuration";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { GroupSessionsSkeleton } from "../SessionListSkeleton";

const SearchXIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m13.5 8.5-5 5" />
    <path d="m8.5 8.5 5 5" />
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

// Phrases rather than the filter bar's labels — these read inside a sentence
// ("No sessions for Period 3 in the last 4 weeks"), and All Time deliberately
// contributes nothing.
const DATE_RANGE_PHRASE = {
  last_4_weeks: "in the last 4 weeks",
  last_8_weeks: "in the last 8 weeks",
  this_school_year: "this school year",
  all_time: "",
};

// Names the filters that produced an empty result, so a correct empty response
// doesn't read as a failure. Returns null when nothing specific can be said.
const describeActiveFilters = (filters, classes) => {
  const selectedClass =
    filters.classroomId != null
      ? (classes || []).find((c) => String(c.id) === String(filters.classroomId))
      : null;
  const className = selectedClass ? classDisplayName(selectedClass) : null;
  const when = filters.startDate
    ? "in the selected dates"
    : DATE_RANGE_PHRASE[filters.dateRange] || "";

  if (!className && !when) return null;
  const subject = className ? `No sessions for ${className}` : "No sessions";
  return when ? `${subject} ${when}.` : `${subject}.`;
};

// How often the list re-reads itself while a teacher sits on the page.
//
// Everything else that refreshes this list is event-driven — a session_status
// push, a reconnect snapshot, the tab being re-shown. None of those fires when
// the piece a teacher is actually waiting for lands: the Solo Time scores on
// the row come from the session replay compilation, and that pipeline publishes
// no status event of its own. So a teacher who stayed on the page watched the
// scores sit unresolved until they reloaded the browser (OKO2-3640).
//
// A minute is chosen against the cost of the read, not the speed of the
// compile: session_history is the slow endpoint on this screen (hence the
// eight-row first page) and each call also reaches Firebase for display names.
const LIST_REFRESH_INTERVAL_MS = 60000;

// Small first page: this endpoint is slow for high-volume educators, and the
// rest arrive on scroll. Raising it costs first paint on exactly the accounts
// that can least afford it.
const LIST_PAGE_SIZE = 8;

const buildQueryString = (filters, page) => {
  const params = new URLSearchParams();
  params.set("page_number", page.toString());
  params.set("page_size", String(LIST_PAGE_SIZE));
  // A transcript deep link names one session; fetching it as a filter keeps the
  // auto-expand from no-oping when the session isn't on the first pages.
  if (filters.sessionId != null) params.set("session_id", filters.sessionId);
  if (filters.classroomId != null) params.set("classroom_id", filters.classroomId);
  if (filters.studentId != null) params.set("student_id", filters.studentId);
  if (filters.standardCode != null) params.set("standard_code", filters.standardCode);
  if (filters.assignmentId != null) params.set("assignment_id", filters.assignmentId);
  if (filters.collaborationLevel != null) params.set("collaboration_level", filters.collaborationLevel.toString());
  if (filters.educatorId) params.set("educator_id", filters.educatorId);
  if (filters.startDate) {
    params.set("start_date", filters.startDate);
    if (filters.endDate) params.set("end_date", filters.endDate);
  } else if (filters.dateRange != null) {
    params.set("date_range", filters.dateRange);
  }
  return params.toString();
};

const trackSessionReportView = (session) =>
  trackViewedPage("Session Report", {
    tab: "Session Summary",
    group_code: session?.group_code ? session.group_code.toUpperCase() : null,
    session_id: session?.session_id,
    session_timestamp: session?.started_at || null,
    standard_id: session?.standard_prefix || session?.standards?.[0]?.standard_code || null,
    // Full addressing set for lesson sessions; standard_id stays scalar
    // for continuity with existing dashboards.
    standard_ids:
      session?.lesson_standard_prefixes?.length > 0
        ? session.lesson_standard_prefixes.map((sp) => sp.standard_prefix)
        : null,
    student_count: Array.isArray(session?.student_collab_levels)
      ? session.student_collab_levels.length
      : null,
  });

export const SessionSummaries = ({
  challengeDataErrors,
  setChallengeDataErrors,
  challengeData,
  setChallengeData,
  isChallengeDataLoading,
  setIsChallengeDataLoading,
  setError,
  setIsLastSessionSummariesPage,
  setIsSessionSummariesLoading,
  isLastSessionSummariesPage,
  isSessionSummariesLoading,
  filters,
  classes,
  highlightSessionId,
  highlightStudentName,
  highlightQuote,
  highlightQuoteId,
  highlightReportTab,
  reportTab,
  statusEvents,
  snapshotCount,
  refreshNonce,
  onSessionEnded,
}) => {
  const [sessionSummaries, setSessionSummaries] = useState([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const loadMoreObserverRef = useRef(null);
  const getSessionDataRef = useRef(null);
  const sessionRefs = useRef({});
  const highlightHandledRef = useRef(false);
  const pageRef = useRef(1);
  const generationRef = useRef(0);
  const isLoadingRef = useRef(false);
  const isLastPageRef = useRef(false);
  // Tracks the last processed session_status push per session so the same
  // event doesn't re-trigger a refresh on every render.
  const processedStatusRef = useRef({});
  // Last live-stream snapshot this mount has reacted to (null = none yet).
  const lastSnapshotRef = useRef(null);
  // session_ids with a challenge-data request currently in flight.
  const challengeInFlightRef = useRef(new Set());

  const getSessionData = useCallback(async () => {
    if (isLastPageRef.current || isLoadingRef.current) return;

    const currentGeneration = generationRef.current;
    const currentPage = pageRef.current;

    try {
      isLoadingRef.current = true;
      setIsSessionSummariesLoading(true);
      const url = `account/session_history?${buildQueryString(filters, currentPage)}`;
      const currentSessionsResponse = await request(url, {
        method: "GET",
      });

      // Discard response if filters changed while request was in-flight
      if (generationRef.current !== currentGeneration) return;

      const currentSessions = currentSessionsResponse.sessions;
      if (currentSessions?.length > 0) {
        // Page 1 can be fetched twice now that an empty first page no longer
        // latches pagination — a soft refresh may have merged those rows in
        // already, and appending them again would double the list.
        setSessionSummaries(prev => {
          const seen = new Set(prev.map(s => s.session_id));
          return [...prev, ...currentSessions.filter(s => !seen.has(s.session_id))];
        });
        pageRef.current = currentPage + 1;
      } else {
        // Only an empty page *after* the first means pagination is finished. An
        // empty first page is a legitimately empty result, and latching on it
        // blocks every later fetch for the lifetime of the filter generation.
        if (currentPage > 1) isLastPageRef.current = true;
        setIsLastSessionSummariesPage(true);
      }
      isLoadingRef.current = false;
      setIsSessionSummariesLoading(false);
      setInitialLoadDone(true);
    } catch (e) {
      if (generationRef.current !== currentGeneration) return;

      const errorMessage = e?.detail || e?.message || "An unknown error occurred.";
      const is5xx = e?.status >= 500 && e?.status < 600;
      logSessionHistory("Session history: Failed to load session list", {
        level: is5xx ? LogLevel.ERROR : LogLevel.WARNING,
        event: "load_error",
        classroomId: filters.classroomId,
        page: currentPage,
        errorMessage,
      });
      if (is5xx) {
        logError("Failed to fetch session history", e, {
          category: LogCategory.ERROR,
        });
      }
      setError(errorMessage);
      isLoadingRef.current = false;
      setIsSessionSummariesLoading(false);
      setInitialLoadDone(true);
    }
  }, [filters, setError, setIsLastSessionSummariesPage, setIsSessionSummariesLoading]);

  // Soft refresh: refetch page 1 and merge into the existing list (replace rows
  // in place, prepend brand-new sessions) without resetting pagination, scroll,
  // or expanded accordions. Used to surface a just-ended ("Analyzing…") session
  // and to update a row when its analysis completes.
  const refreshFirstPage = useCallback(async () => {
    const currentGeneration = generationRef.current;
    try {
      const url = `account/session_history?${buildQueryString(filters, 1)}`;
      const resp = await request(url, { method: "GET" });
      if (generationRef.current !== currentGeneration) return;
      const fresh = resp?.sessions || [];
      if (fresh.length === 0) return;
      setSessionSummaries((prev) => {
        const freshById = new Map(fresh.map((s) => [s.session_id, s]));
        const merged = prev.map((s) =>
          freshById.has(s.session_id) ? freshById.get(s.session_id) : s,
        );
        const existingIds = new Set(prev.map((s) => s.session_id));
        const newOnes = fresh.filter((s) => !existingIds.has(s.session_id));
        return [...newOnes, ...merged];
      });
    } catch (e) {
      // Non-fatal: the normal (re)connect history fetch is the source of truth.
      logError("Failed to refresh session history after status event", e, {
        category: LogCategory.ERROR,
      });
    }
  }, [filters]);

  useEffect(() => {
    getSessionDataRef.current = getSessionData;
  }, [getSessionData]);

  // ---- Cross-mount list cache (OKO2-3684) ----
  // The loaded pages live in the query cache keyed by the filter set, so a
  // return navigation paints the previous list instantly instead of a spinner;
  // refreshFirstPage() then revalidates the top of the list in place with the
  // same merge the live-session pushes use. The pagination flow itself is
  // untouched — this only seeds and mirrors its state.
  const queryClient = useQueryClient();
  const listCacheKey = useMemo(
    () => ["session-history-list", JSON.stringify(filters)],
    [filters],
  );

  // Ref, not state: the initial-fetch effect below runs in the same effect
  // pass as hydration and would still see the pre-hydration state — the ref is
  // its synchronous signal that the list is already populated, so it must not
  // start a fetch (and its spinner) over a cache hit.
  const hydratedFromCacheRef = useRef(false);

  // The cache key the current sessionSummaries were loaded under. On a
  // filter-change render the mirror effect below fires (its key changed)
  // before the reset effect has cleared the old list — without this guard it
  // writes the old filter's rows under the new filter's key, and hydration
  // then serves them back, so the list never changes.
  const loadedKeyRef = useRef(null);

  const hydrateFromCache = useCallback(() => {
    const cached = queryClient.getQueryData(listCacheKey);
    if (!cached?.sessions?.length) return false;
    setSessionSummaries(cached.sessions);
    pageRef.current = cached.page;
    isLastPageRef.current = cached.isLastPage;
    setIsLastSessionSummariesPage(cached.isLastPage);
    setInitialLoadDone(true);
    return true;
  }, [queryClient, listCacheKey, setIsLastSessionSummariesPage]);

  // Mirror the loaded state into the cache. Gated on initialLoadDone so the
  // empty pre-fetch state never clobbers a good entry. Only the FIRST page is
  // kept: it is what paints above the fold on return, and it is the slice the
  // revalidating page-1 merge actually freshens — deeper rows re-fetch on
  // scroll instead of ever being served stale.
  useEffect(() => {
    if (!initialLoadDone) return;
    if (loadedKeyRef.current !== listCacheKey) return;
    queryClient.setQueryData(listCacheKey, {
      sessions: sessionSummaries.slice(0, LIST_PAGE_SIZE),
      page: 2,
      isLastPage: sessionSummaries.length <= LIST_PAGE_SIZE && isLastPageRef.current,
    });
  }, [sessionSummaries, initialLoadDone, listCacheKey, queryClient]);

  // Reset state when any filter changes
  useEffect(() => {
    loadedKeyRef.current = listCacheKey;
    // Initial mount: no reset needed, but a cached list for these filters can
    // skip the loading state entirely — revalidated in place.
    if (generationRef.current === 0) {
      generationRef.current = 1;
      hydratedFromCacheRef.current = hydrateFromCache();
      if (hydratedFromCacheRef.current) refreshFirstPage();
      return;
    }
    generationRef.current += 1;
    pageRef.current = 1;
    isLastPageRef.current = false;
    isLoadingRef.current = false;
    setSessionSummaries([]);
    setIsLastSessionSummariesPage(false);
    setIsSessionSummariesLoading(false);
    setInitialLoadDone(false);
    hydratedFromCacheRef.current = hydrateFromCache();
    if (hydratedFromCacheRef.current) refreshFirstPage();
  }, [filters, setIsLastSessionSummariesPage, setIsSessionSummariesLoading, hydrateFromCache, refreshFirstPage]);

  // Fetch data when summaries are empty and load hasn't been done. The ref
  // (not the state, which is one commit behind in this pass) says a cache hit
  // already populated the list — its refreshFirstPage() revalidation owns the
  // freshness, so starting a paged fetch here would just flash the spinner.
  useEffect(() => {
    if (sessionSummaries.length === 0 && !initialLoadDone && !hydratedFromCacheRef.current) {
      getSessionData();
    }
  }, [getSessionData, sessionSummaries, initialLoadDone]);

  // Fetches the per-question challenge data that powers the Question Detail
  // carousel (SessionHistoryChallenge). Skips the request when data is already
  // cached for the session unless `force` is set (e.g. a session just completed).
  const fetchChallengeData = async (sessionId, index, force = false) => {
    // A single accordion click reaches here twice (Accordion onSelect and the
    // header's own onClick) before setChallengeData lands, so the cache check
    // alone can't dedupe — track in-flight sessions in a ref.
    if (challengeInFlightRef.current.has(sessionId)) return;
    if (force || !challengeData[sessionId] || (challengeData[sessionId] && challengeData[sessionId].length === 0)) {
      challengeInFlightRef.current.add(sessionId);
      try {
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: true }));
        const response = await request(`account/session_history_challenges/${sessionId}`, {
          method: "GET",
        });
        setChallengeData(prev => ({ ...prev, [sessionId]: response }));
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: false }));
      } catch (e) {
        logError("Failed to fetch challenge data", e, {
          category: LogCategory.ERROR,
          sessionId,
        });
        const errorMessage = e?.detail || e?.message || "An error occurred while fetching challenge data.";
        logSessionHistory("Session history: Failed to load challenge data", {
          level: LogLevel.ERROR,
          event: "load_error",
          sessionId,
          errorMessage,
        });
        setChallengeDataErrors(prev => ({ ...prev, [sessionId]: errorMessage }));
        setIsChallengeDataLoading(prev => ({ ...prev, [index]: false }));
      } finally {
        challengeInFlightRef.current.delete(sessionId);
      }
    }
  };

  // React to best-effort session_status push hints from the live SSE stream.
  // processing/failed/completed all refresh the history list (so a just-ended
  // session appears as "Analyzing…" and a finished one updates its row); for
  // `completed` we additionally refetch per-question challenge data, but only
  // for sessions whose detail was already opened (others fetch on expand).
  useEffect(() => {
    if (!statusEvents || !initialLoadDone) return;
    let needsListRefresh = false;
    for (const [sid, ev] of Object.entries(statusEvents)) {
      const prev = processedStatusRef.current[sid];
      if (prev && prev.status === ev.status && prev.updated_at === ev.updated_at) {
        continue;
      }
      processedStatusRef.current[sid] = ev;
      needsListRefresh = true;
      if (ev.status === "completed" && challengeData[sid] !== undefined) {
        const idx = sessionSummaries.findIndex((s) => s.session_id === sid);
        fetchChallengeData(sid, idx >= 0 ? idx : 0, true);
      }
    }
    if (needsListRefresh) refreshFirstPage();
    // initialLoadDone is a dep so a push arriving mid-initial-fetch is replayed
    // once the list is ready, instead of being dropped for good.
  }, [statusEvents, initialLoadDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // A reconnect (idle watchdog, tab restored, network back) replays an
  // authoritative snapshot, but the snapshot carries live sessions only — the
  // list would otherwise keep whatever it held while the stream was down, and
  // any session_status pushed during that gap is gone for good. The first
  // snapshot of a mount is skipped: the initial fetch has just run.
  useEffect(() => {
    if (!snapshotCount) return;
    if (lastSnapshotRef.current === snapshotCount) return;
    const isFirst = lastSnapshotRef.current === null;
    lastSnapshotRef.current = snapshotCount;
    if (!isFirst) refreshFirstPage();
  }, [snapshotCount, refreshFirstPage]);

  // The page bumps this when the teacher ends a session from it.
  useEffect(() => {
    if (refreshNonce) refreshFirstPage();
  }, [refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // The list is otherwise fetched once per filter generation, so a tab left open
  // keeps whatever it first loaded. Re-check when the tab comes back into view:
  // merge into a populated list (no flicker), full reset when there's nothing on
  // screen so pagination restarts cleanly.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (sessionSummaries.length > 0) {
        refreshFirstPage();
        return;
      }
      generationRef.current += 1;
      pageRef.current = 1;
      isLastPageRef.current = false;
      isLoadingRef.current = false;
      setIsLastSessionSummariesPage(false);
      setInitialLoadDone(false);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [sessionSummaries.length, refreshFirstPage, setIsLastSessionSummariesPage]);

  // The event-driven refreshes above all need something to happen: a push, a
  // reconnect, the teacher leaving and coming back. Sitting still on the page
  // is none of those, so the list also re-reads itself on a timer.
  //
  // Only while the page is on screen. A hidden tab is already covered by the
  // visibility handler above, which refreshes the moment it is shown again —
  // polling it as well would buy nothing and pay for it every minute.
  useEffect(() => {
    if (!initialLoadDone) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      refreshFirstPage();
    }, LIST_REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [initialLoadDone, refreshFirstPage]);

  // Reset highlight tracking when session id changes
  useEffect(() => {
    highlightHandledRef.current = false;
  }, [highlightSessionId]);

  // Auto-expand and scroll to highlighted session.
  useEffect(() => {
    if (!highlightSessionId || highlightHandledRef.current || sessionSummaries.length === 0) return;
    const index = sessionSummaries.findIndex(s => s.session_id === highlightSessionId);
    if (index === -1) return;
    highlightHandledRef.current = true;
    setActiveKey(index);
    fetchChallengeData(sessionSummaries[index].session_id, index);
    // A deep-linked session is opened for the teacher, so it counts as a view
    // of its report exactly like a click. The handled ref keeps it to once.
    trackSessionReportView(sessionSummaries[index]);
    setTimeout(() => {
      const el = sessionRefs.current[highlightSessionId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 500);
  }, [sessionSummaries, highlightSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Callback ref, not a mount-once effect: the trigger sits inside the list,
  // which is not rendered during a cold load (skeleton) or a filter reset, so a
  // ref captured at mount is null and the list never paginates. Observing on
  // every (re)mount of the node keeps infinite scroll alive across those.
  const loadMoreRef = useCallback((node) => {
    loadMoreObserverRef.current?.disconnect();
    loadMoreObserverRef.current = null;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) getSessionDataRef.current?.();
      },
      { threshold: 0.1, rootMargin: "100px" },
    );
    observer.observe(node);
    loadMoreObserverRef.current = observer;
  }, []);

  const handleAccordionSelect = (eventKey) => {
    setActiveKey(eventKey);
    const index = parseInt(eventKey);
    if (!isNaN(index)) {
      const session = sessionSummaries[index];
      const sessionId = session?.session_id;
      if (sessionId) {
        fetchChallengeData(sessionId, index);
        trackSessionReportView(session);
      }
    }
  };

  const updateSessionEndedAt = (sessionId, endedAt) => {
    setSessionSummaries(prev =>
      prev.map(session =>
        session.session_id === sessionId
          ? { ...session, ended_at: endedAt }
          : session
      )
    );
    onSessionEnded?.(sessionId);
  };

  const showEmptyState =
    initialLoadDone &&
    !isSessionSummariesLoading &&
    sessionSummaries.length === 0;
  const emptyStateDescription = showEmptyState ? describeActiveFilters(filters, classes) : null;

  return (
    <>
      {!initialLoadDone && sessionSummaries.length === 0 ? (
        // Cold load (no cache hit): the list's shape instead of a spinner.
        <GroupSessionsSkeleton />
      ) : showEmptyState ? (
        <EmptyState>
          <SearchXIcon />
          <EmptyStateTitle>No Sessions Found</EmptyStateTitle>
          <EmptyStateText>
            {emptyStateDescription || "We couldn't find any group session reports that match your selected filters."}
            <br />
            Try a wider date range, or choose a different class.
          </EmptyStateText>
        </EmptyState>
      ) : (
        <Accordion activeKey={activeKey} onSelect={(key) => handleAccordionSelect(key)}>
          <Wrapper>
            {sessionSummaries.map((value, index) => {
              const durationMs = sessionDurationMs(value);
              return (
                <SessionSummary
                  ref={el => { sessionRefs.current[value.session_id] = el; }}
                  handleAccordionSelect={handleAccordionSelect}
                  key={value.session_id}
                  value={value}
                  index={index}
                  autoExpand={highlightSessionId === value.session_id}
                  autoExpandStudentName={highlightSessionId === value.session_id ? highlightStudentName : null}
                  autoExpandQuote={highlightSessionId === value.session_id ? highlightQuote : null}
                  autoExpandQuoteId={highlightSessionId === value.session_id ? highlightQuoteId : null}
                  autoExpandReportTab={highlightSessionId === value.session_id ? highlightReportTab : null}
                  reportTab={highlightSessionId === value.session_id ? reportTab : null}
                  durationMs={durationMs}
                  challengeDataErrors={challengeDataErrors}
                  setChallengeDataErrors={setChallengeDataErrors}
                  challengeData={challengeData}
                  setChallengeData={setChallengeData}
                  isChallengeDataLoading={isChallengeDataLoading}
                  setIsChallengeDataLoading={setIsChallengeDataLoading}
                  fetchChallengeData={fetchChallengeData}
                  updateSessionEndedAt={updateSessionEndedAt}
                  educatorId={filters.educatorId}
                />
              );
            })}
            {/* Load-more feedback lives at the list's tail now, where the new
                rows will appear — the old fixed center-screen spinner is gone. */}
            {isSessionSummariesLoading && sessionSummaries.length > 0 && (
              <GroupSessionsSkeleton rows={1} />
            )}
            <LoadMoreTrigger ref={loadMoreRef} />
          </Wrapper>
        </Accordion>
      )}
    </>
  );
};

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const LoadMoreTrigger = styled.div`
  height: 20px;
  width: 100%;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 60px 0;
`;

const EmptyStateTitle = styled.h3`
  font-family: "ABC Marfa", sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: ${colors.nightlyWoods950};
  margin: 0;
`;

const EmptyStateText = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${colors.nightlyWoods950};
  text-align: center;
  letter-spacing: 0.25px;
  margin: 0;
`;
